import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import { PortalDsEmptyState } from 'src/components/portal-design-system/portal-ds-empty-state';
import { useGetReportingApiV1FlightsMutation } from 'src/redux/admin/reporting-api-v1/reporting-api-v1.endpoints';
import { FlightEntityLevel } from 'src/redux/admin/reporting/reporting.types';
import { formatPlatformLabel } from 'src/pages/reports/utils/format-reporting-labels';
import { useSelector } from 'react-redux';
import { ReduxState } from 'src/redux/redux-state';
import { RootState } from 'src/redux/redux-store';
import FlightsFilterBar, { FlightsFilterSelection } from '../components/flights-filter-bar';
import { FlightsTable } from '../components/flights-table';

const sanitizeClientId = (value?: string | null): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  return trimmed;
};

const ENTITY_LEVELS: { id: FlightEntityLevel; label: string }[] = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'line', label: 'Line item' },
];

const FlightsTab = () => {
  const userState = useSelector((state: ReduxState) => state.userState);
  const activeClient = useSelector((state: RootState) => state.userState.activeClient);
  const urlParams = new URLSearchParams(location.search);
  const previewClientId = sanitizeClientId(urlParams.get('previewClientId'));
  const isClientView =
    userState.permissions?.groups?.includes('client_user') ||
    userState.permissions?.groups?.includes('client_admin');
  const extClientId = isClientView
    ? undefined
    : previewClientId || sanitizeClientId(activeClient?.ext_client_id);

  const [selection, setSelection] = useState<FlightsFilterSelection>({
    source: '',
    accounts: [],
  });
  const [entityLevel, setEntityLevel] = useState<FlightEntityLevel>('campaign');
  const [isFlightsLoading, setIsFlightsLoading] = useState(false);
  const [fetchFlights, { data: flightRows = [] }] = useGetReportingApiV1FlightsMutation();

  const canLoad = Boolean(selection.source && selection.accounts.length > 0);

  const loadFlights = useCallback(async(signal: { cancelled: boolean }) => {
    if (!selection.source || selection.accounts.length === 0) {
      if (!signal.cancelled) {
        setIsFlightsLoading(false);
      }
      return;
    }

    setIsFlightsLoading(true);
    try {
      await fetchFlights({
        source: selection.source,
        accountIds: selection.accounts.map((account) => account.id),
        entityLevel,
        extClientId,
      }).unwrap();
    } catch {
      // Keep prior rows visible on error; loading state still clears below.
    } finally {
      if (!signal.cancelled) {
        setIsFlightsLoading(false);
      }
    }
  }, [selection.source, selection.accounts, entityLevel, fetchFlights, extClientId]);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadFlights(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadFlights]);

  const subtitle = useMemo(() => {
    if (!canLoad) return 'Select a platform and accounts to load flight dates and budgets.';
    const accountLabel = selection.accounts.length === 1
      ? selection.accounts[0].name
      : `${selection.accounts.length} accounts`;
    return `${formatPlatformLabel(selection.source)} · ${accountLabel} · Active in the last 30 days`;
  }, [canLoad, selection]);

  const showTable = canLoad;
  const tableLoading = showTable && isFlightsLoading;

  return (
    <div className="h-full overflow-y-auto bg-[#F4F5F8] px-6 py-6 sm:px-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold leading-tight tracking-[-0.6px] text-[#0D0F1A]">
            Flights
          </h1>
          <p className="mt-1 text-[13px] text-[#5E6278]">
            Campaign and line-item status, flight dates, lifetime budget, and daily budget
            for flights active in the last 30 days.
          </p>
        </div>
        <div
          className={portalDs.segmentGroup}
          role="tablist"
          aria-label="Entity level"
        >
          {ENTITY_LEVELS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={entityLevel === option.id}
              className={cn(
                portalDs.segment,
                entityLevel === option.id && portalDs.segmentActive
              )}
              onClick={() => {
                if (entityLevel === option.id) return;
                setEntityLevel(option.id);
                setIsFlightsLoading(true);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <FlightsFilterBar onSelectionChange={setSelection} />

      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <div className={portalDs.sectionLabel}>
            {entityLevel === 'campaign' ? 'Campaign flights' : 'Line item flights'}
          </div>
          <p className="mt-1 text-[13px] text-[#5E6278]">
            {subtitle}
          </p>
        </div>
        {showTable && !tableLoading ? (
          <span className={portalDs.rowCount}>
            {flightRows.length}
            {' '}
            {flightRows.length === 1 ? 'row' : 'rows'}
          </span>
        ) : null}
      </div>

      {showTable ? (
        <FlightsTable
          rows={flightRows}
          entityLevel={entityLevel}
          isLoading={tableLoading}
        />
      ) : (
        <div className={portalDs.tableContainer}>
          <PortalDsEmptyState
            icon={<CalendarRange className="h-5 w-5" />}
            title="Select accounts to view flights"
            description="Choose a platform and one or more accounts. Toggle between campaign and line item to see flight dates and budgets for the last 30 days."
          />
        </div>
      )}
    </div>
  );
};

export default FlightsTab;
